import { RouteConfig } from '../types';
import * as Components from '../lazyComponents';

/**
 * Task #672-1 — Sprint 6 Network Console UI routes.
 *
 * Route gate strategy:
 *   - requiresAuth: enforced by AuthGuard wrapper in envolverRuta (App.tsx).
 *   - roles: limited to tenant-admin level roles whose tenants can hold the
 *     `coordinador_red` addon — backend re-validates the addon on every call
 *     so this client-side filter is defense-in-depth, not the authoritative
 *     gate.
 *   - The NetworkMemberGuard on the API enforces that the JWT tenant actually
 *     belongs to the network passed via :networkId. If a user with a valid
 *     role visits a network they are NOT a member of, the page renders the
 *     "no acceso" panel (NetworkDashboardPage handles 403 gracefully).
 *
 * Subsequent Sprint 6 pages (members, lead-routing, regalías, reports,
 * settings) will register their own RouteConfig entries here as Tasks #672-2
 * through #672-6 land.
 */
export const networkRoutes: RouteConfig[] = [
  {
    path: '/network/:networkId/dashboard',
    element: <Components.NetworkDashboardPage />,
    // Tenant-admin roles that own networks (owner of franquicia matriz) or
    // can administer a region (NetworkMemberRole.ADMIN). Server-side guards
    // re-verify; this is just to keep the menu surface coherent.
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'PROJECT_DEVELOPER_ADMIN',
      'CONDOMINIUM_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Task #672-2 — members + invitations roster.
  // Auth-gated (any authenticated tenant role). NetworkMemberGuard backend
  // ensures the caller is a member of :networkId; UI hides Action menus when
  // viewerRole !== 'owner' (see NetworkMembersPage `viewerCanManage`).
  {
    path: '/network/:networkId/members',
    element: <Components.NetworkMembersPage />,
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'AGENCY_AGENT_USER',
      'PROJECT_DEVELOPER_ADMIN',
      'CONDOMINIUM_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Task #672-3 — lead routing configuration + live monitor.
  // Owner/admin scope on backend (NetworkOwnerOnly / NetworkAdminOrOwner).
  // Frontend roles list mirrors dashboard; NetworkMemberGuard enforces network
  // membership and NetworkOwner/Admin decorator enforces sub-role on every call.
  {
    path: '/network/:networkId/lead-routing',
    element: <Components.NetworkLeadRoutingPage />,
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'PROJECT_DEVELOPER_ADMIN',
      'CONDOMINIUM_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Task #672-4 — regalías operational view (owner/admin scope).
  // Shares the FinancialSummaryReport DTO with the Reports → Financial tab,
  // but framed around overdue regalias + per-tenant outstanding balances.
  // Backend NetworkReportingController.getFinancialSummary +
  // downloadRegaliasCsv enforce assertOwnerOrAdmin() server-side.
  {
    path: '/network/:networkId/regalias',
    element: <Components.NetworkRegaliasPage />,
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'PROJECT_DEVELOPER_ADMIN',
      'BROKER_MANAGER_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Task #672-5 — performance / lead-routing analytics / financial summary reports.
  // Backend NetworkReportingController gates each call via assertOwnerOrAdmin().
  // A user with the role surface filter below but not owner/admin of this
  // network will hit the page and get 403 banners on each fetch (page handles).
  {
    path: '/network/:networkId/reports',
    element: <Components.NetworkReportsPage />,
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'PROJECT_DEVELOPER_ADMIN',
      'CONDOMINIUM_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
  // Task #672-6 — owner configuration console (6 tabs: general / branding /
  // regalía / domain / lead defaults / danger zone). Backend NetworkController
  // PATCH /:id is NetworkOwnerOnly + DELETE is owner-only (archive). The page
  // surfaces a graceful 403 banner via loadNetwork() error handling.
  {
    path: '/network/:networkId/settings',
    element: <Components.NetworkSettingsPage />,
    roles: [
      'SUPER_ADMIN',
      'AGENCY_ADMIN',
      'PROJECT_DEVELOPER_ADMIN',
      'BROKER_MANAGER_ADMIN',
    ],
    requiresAuth: true,
    requiresLayout: true,
  },
];
